/**
 * Undo for calendar changes (DESIGN.md §18.2: reversible actions run with an undo toast):
 * - delete/cancel is delayed while the toast shows (the event disappears at once; Undo keeps it, so a cancellation
 *   mail is only sent when the user did not undo); leaving the page sends what is still pending;
 * - an answer (accept/tentative/decline) can be changed back to the previous answer.
 */
import { useI18n } from '@platform/i18n';
import { toast, useErrorText } from '@platform/ui';
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { api, type CalendarEvent, type CalendarEventScope } from '../api';
import { useCalendar } from './calendar-context';

/** How long Undo is offered before a delete is sent. */
export const UNDO_MS = 6000;

type Pending = { run: () => Promise<void>; timer: ReturnType<typeof setTimeout> };
const pending = new Map<string, Pending>();
const listeners = new Set<() => void>();
let snapshot: ReadonlySet<string> = new Set();
const emit = () => {
  snapshot = new Set(pending.keys());
  for (const listener of listeners) listener();
};
const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/** Sends every pending delete now (page hide, tests). */
export async function flushPendingDeletes(): Promise<void> {
  const all = [...pending.values()];
  for (const p of all) clearTimeout(p.timer);
  pending.clear();
  emit();
  await Promise.allSettled(all.map((p) => p.run()));
}

/** Event ids whose delete is waiting for the undo time (hidden from the grid). */
export function usePendingDeletes(): ReadonlySet<string> {
  return useSyncExternalStore(subscribe, () => snapshot, () => snapshot);
}

type Answer = 'accept' | 'tentative' | 'decline';
const answerOf = (response: CalendarEvent['myResponse']): Answer | undefined =>
  response === 'accepted' ? 'accept' : response === 'tentative' ? 'tentative' : response === 'declined' ? 'decline' : undefined;

export function useUndoableCalendar() {
  const { t } = useI18n();
  const errorText = useErrorText();
  const { changed, setSelectedId } = useCalendar();

  useEffect(() => {
    const onHide = () => void flushPendingDeletes();
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  /** Delete (organizer of a meeting: cancel) after the undo time. */
  const remove = useCallback(
    (event: CalendarEvent, options: { scope: CalendarEventScope; notify?: boolean; comment?: string; title: string }) => {
      const key = event.id;
      const run = async () => {
        try {
          await api.deleteEvent(event.id, { scope: options.scope, notify: options.notify ?? true, ...(options.comment && { comment: options.comment }) });
        } catch (error) {
          errorText.notify(error);
        } finally {
          changed();
        }
      };
      clearTimeout(pending.get(key)?.timer);
      pending.set(key, {
        run,
        timer: setTimeout(() => {
          pending.delete(key);
          emit();
          void run();
        }, UNDO_MS),
      });
      emit();
      setSelectedId(undefined);
      toast.success(t(event.isMeeting && event.isOrganizer ? 'webmail.calendar.cancelledToast' : 'webmail.calendar.deletedToast'), {
        duration: UNDO_MS,
        action: {
          label: t('common.undo'),
          onClick: () => {
            clearTimeout(pending.get(key)?.timer);
            pending.delete(key);
            emit();
          },
        },
      });
    },
    [changed, errorText, setSelectedId, t],
  );

  /** Accept/tentative/decline with an optional message, or without telling the organizer; Undo restores the previous answer. */
  const respond = useCallback(
    async (event: CalendarEvent, response: Answer, options: { comment?: string; send?: boolean } = {}) => {
      try {
        const id = await api.respondToEvent(event.id, { response, sendResponse: options.send ?? true, ...(options.comment && { comment: options.comment }) });
        const previous = answerOf(event.myResponse);
        toast.success(t(`webmail.calendar.respondedToast.${response}`), {
          ...(previous && previous !== response && id !== null && {
            action: {
              label: t('common.undo'),
              onClick: () => {
                api.respondToEvent(id, { response: previous, sendResponse: options.send ?? true }).then(changed, (error: unknown) => errorText.notify(error));
              },
            },
          }),
        });
        changed();
        return true;
      } catch (error) {
        errorText.notify(error);
        return false;
      }
    },
    [changed, errorText, t],
  );

  return { remove, respond };
}
